import { Component, Input, OnInit, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/services/api/api.service';
import { NotificationService } from 'src/app/services/notification.service';
import { TabControlService } from 'src/app/services/tab-control.service';
import { environment } from 'src/environments/environment';
import * as Constants from "src/app/constants/constants";

@Component({
  selector: 'app-shares',
  templateUrl: './shares.component.html',
  styleUrls: ['./shares.component.scss']
})
export class SharesComponent implements OnInit {
  @Input() showSharesPopup = signal(false);
  @Input() areSharesAwarded = signal(false);

  // We track the original pool total (e.g., 100)
  readonly TOTAL_POOL = 100;
  maxShares = signal(this.TOTAL_POOL);
  minShares = signal(1);

  sharesForm!: FormGroup;
  
  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly apiService: ApiService,
    private readonly tabControlService: TabControlService,
    private readonly notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.trackFormChanges();
  }

  // FormArray getter for easier template binding
  get beneficiariesFormArray(): FormArray {
    return this.sharesForm.get('beneficiaries') as FormArray;
  }

  // Close the popup modal
  closePopup(): void {
    this.showSharesPopup.set(false);
  }

  // Initialize the Form structure
  private initForm(): void {
    const beneficiaryGroups = this.tabControlService.beneficiaries.map((b: any) => 
      this._formBuilder.group({
        id: [b.BeneficiaryID], // assuming your beneficiary model has an identifier
        name: [{ value: b.Category === 'Individual' ? `${b.FirstName} ${b.LastName}` : b.InstitutionName, disabled: true }],
        PercShare: [0, [Validators.required, Validators.min(0)]]
      })
    );

    this.sharesForm = this._formBuilder.group({
      beneficiaries: this._formBuilder.array(beneficiaryGroups)
    });
  }

  // Monitor share changes and update remaining maxShares dynamically
  private trackFormChanges(): void {
    this.sharesForm.get('beneficiaries')?.valueChanges.subscribe((values: any[]) => {
      const allocatedShares = values.reduce((sum, item) => sum + (Number(item.PercShare) || 0), 0);
      
      // Update the available remaining shares signal
      const remaining = this.TOTAL_POOL - allocatedShares;
      this.maxShares.set(Math.max(remaining, 0));

      // Dynamically update validators for each control so they can't exceed pool limit individually
      this.beneficiariesFormArray.controls.forEach(control => {
        const currentControlValue = control.get('PercShare')?.value || 0;
        const allowedMaxForThisControl = remaining + currentControlValue;
        
        control.get('PercShare')?.setValidators([
          Validators.required, 
          Validators.min(this.minShares()), 
          Validators.max(allowedMaxForThisControl)
        ]);
        control.get('PercShare')?.updateValueAndValidity({ emitEvent: false });
      });
    });
  }

  organizeValues(items: any): string {
    try {
      if (typeof items == 'string') {
        return items;
      }
      
      let output: string = items[0];
      for (let i=1; i<items.length; i++) {
        output = output + ', ' +items[i];
      }
      return output;
    } catch (error) {
      console.log('Error : ', error);
      return '';
    }
  }
  cleanupPayload(payload: any) {
    if (payload.hasOwnProperty('TrustPurpose')) {
      let purpose = this.organizeValues(payload.TrustPurpose);

      if (payload.OtherPurpose && payload.OtherPurpose.length > 1) {
        purpose = `${purpose}: ${payload.OtherPurpose}`;
      }
      payload.TrustPurpose = purpose;
    }
    
    if (payload.hasOwnProperty('IDNoImage')) {
      payload.IDNoImage = {name: '', stringValue: ''};
    }
    if (payload.hasOwnProperty('KraPinImage')) {
      payload.KraPinImage = {name: '', stringValue: ''};
    }
    if (payload.hasOwnProperty('RegCertImage')) {
      payload.RegCertImage = {name: '', stringValue: ''};
    }
    if (payload.hasOwnProperty('BirthCertImage')) {
      payload.BirthCertImage = {name: '', stringValue: ''};
    }

    if (payload.hasOwnProperty('IDURL')) {
      delete payload.IDURL;
    }
    if (payload.hasOwnProperty('KraURL')) {
      delete payload.KraURL;
    }
    if (payload.hasOwnProperty('RegCertURL')) {
      delete payload.RegCertURL;
    }
    if (payload.hasOwnProperty('BirthCertURL')) {
      delete payload.BirthCertURL;
    }
    
    if (payload.hasOwnProperty('SavedID')) {
      delete payload.SavedID;
    }
    if (payload.hasOwnProperty('SavedPIN')) {
      delete payload.SavedPIN;
    }
    if (payload.hasOwnProperty('SavedRegCert')) {
      delete payload.SavedRegCert;
    }
    if (payload.hasOwnProperty('SavedBirthCert')) {
      delete payload.SavedBirthCert;
    }
    
    if (payload.hasOwnProperty('ContactPersons')) {
      payload.ContactPersons = [];
    }
    if (payload.hasOwnProperty('done')) {
      delete payload.done;
    }
    if (payload.hasOwnProperty('contactsDone')) {
      delete payload.contactsDone;
    }
    if (payload.hasOwnProperty('personalDone')) {
      delete payload.personalDone;
    }

    return payload;
  }

  async updateBeneficiariesShares() {
    if (this.sharesForm.invalid) {
      this.notificationService.viewToast('error', 'Form Inputs Error', 'Please correct the validation errors before saving.');
      return;
    }
    if (this.maxShares() > 0) {
      this.notificationService.viewToast('error', 'Form Inputs Error', 'Please assign all shares first');
      return;
    }
    try {
      this.notificationService.isLoading(true);
      const formValues = this.beneficiariesFormArray.getRawValue();
      for (const item of formValues) {
        const data = {
          ...this.tabControlService.beneficiaries.find((b: any) => b.BeneficiaryID === item.id),
          PercShare: item.PercShare
        };

        const payload = this.cleanupPayload(data);

        if (!environment.production) {
          console.log('Payload to update shares for beneficiary ID ', item.id, ': ', payload);
        }
        const response = await this.apiService.postRequest(
          environment.baseUrl + Constants.saveBenURL, payload);

        if (response.Status !== 1) {
          console.error('Failed to update shares : ', response);
          this.notificationService.viewToast('error', 'An Error Occured', `Failed to update shares for ${payload.name}.`);
          return;// Exit on first failure
        }

        const beneficiary = this.tabControlService.beneficiaries.find((b: any) => b.BeneficiaryID === item.id);
        if (beneficiary) {
          const index = this.tabControlService.beneficiaries.indexOf(beneficiary);
          const updatedRecord = {
            ...beneficiary,
            ...{PercShare: item.PercShare}
          };
          console.log('Updated beneficiary record after share update: ', updatedRecord);
          Object.assign(this.tabControlService.beneficiaries[index], updatedRecord);
          await this.tabControlService.updateRecord(3, this.tabControlService.beneficiaries);
        }  
      }
      this.areSharesAwarded.set(true);
      this.notificationService.viewToast('success', 'Shares updated successfully!');
      this.closePopup();
      
    } catch (error) {
      this.notificationService.viewToast('error', 'Error', 'An error occurred while saving the shares.');
      console.error('Error updating shares:', error);
    } finally {
      this.notificationService.isLoading(false);
    }
  }
}
